"use client";

import { authFetch } from "@/lib/authFetch";
import { useEffect, useState, type PropsWithChildren } from "react";
import dynamic from "next/dynamic";
import { AnalyticsScripts } from "@/lib/analytics";
import { AnalyticsConsentProvider } from "@/lib/analyticsConsentContext";
import { AnalyticsConsentBanner } from "@/components/analytics/AnalyticsConsentBanner";
import AuthSessionProvider from "./auth/AuthSessionProvider";
import { ProfileCompleteGate } from "./account/ProfileCompleteGate";
import { GruvShell } from "@/components/gruv/GruvShell";
import { BrowserConsoleGate } from "./BrowserConsoleGate";

const EventSearchOverlay = dynamic(
  () => import("@/components/events/EventSearchOverlay").then((mod) => mod.EventSearchOverlay),
  {
    loading: () => null,
    ssr: false,
  },
);

type ProvidersProps = PropsWithChildren<{
  /** From root layout: provinces with listable events via internal EVENTS_URL */
  initialEventProvinces?: string[];
}>;

export default function Providers({ children, initialEventProvinces = [] }: ProvidersProps) {
  const [eventProvinces, setEventProvinces] = useState(initialEventProvinces);

  useEffect(() => {
    let active = true;
    void authFetch("/api/events/meta/provinces", { cache: "default" })
      .then(async (response): Promise<{ provinces?: unknown }> => response.ok ? response.json() : {})
      .then((payload) => {
        if (!active || !Array.isArray(payload.provinces)) return;
        const provinces = payload.provinces.filter((value: unknown): value is string => typeof value === "string" && value.length <= 100);
        setEventProvinces(Array.from(new Set(provinces)).slice(0, 100));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  return (
    <AnalyticsConsentProvider>
      <BrowserConsoleGate />
      <AnalyticsScripts />
      <AnalyticsConsentBanner />
      <AuthSessionProvider>
        <ProfileCompleteGate>
          <GruvShell initialEventProvinces={eventProvinces} searchOverlay={<EventSearchOverlay />}>
            <main className="gruv-main">
              <div className="gruv-container">{children}</div>
            </main>
          </GruvShell>
        </ProfileCompleteGate>
      </AuthSessionProvider>
    </AnalyticsConsentProvider>
  );
}
